import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentJointFinancesData,
  type EnrollmentJointFinancesSubmitInput,
  type EnrollmentJointFinancesSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Joint Finances data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointFinancesData>
 * @example const response = await getEnrollmentJointFinancesData()
 */
export const getEnrollmentJointFinancesData = async (
  context?: DefaultContext,
): Promise<EnrollmentJointFinancesData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentJointFinancesData',
      query: `mutation getEnrollmentJointFinancesData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          accountType
          activePerson {
            applicationId
            path
            progress
            name {
              firstName
              lastName
            }
            isInitiator
            maritalStatus
            taxFilingStatus
            personalAnnualGrossIncome
            personalNetWorthRange
          }
        }
      }`,
    },
    {
      input: {},
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointFinancesData }
  return response.submitJointEnrollment
}

export const useGetEnrollmentJointFinancesData = createQueryHook<undefined, EnrollmentJointFinancesData>(
  getEnrollmentJointFinancesData,
)

/**
 * Submit Enrollment Joint Finances data
 * @param enrollmentFinancesSubmitInput - the input data for finances submission
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointFinancesSubmitOutput>
 * @example const response = await submitEnrollmentJointFinancesData();
 */
export const submitEnrollmentJointFinancesData = async (
  enrollmentFinancesSubmitInput: EnrollmentJointFinancesSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentJointFinancesSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointFinancesData',
      query: `mutation submitEnrollmentJointFinancesData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            path
          }
        }
      }`,
    },
    {
      input: enrollmentFinancesSubmitInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointFinancesSubmitOutput }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointFinancesData = createMutationHook<
  EnrollmentJointFinancesSubmitInput,
  EnrollmentJointFinancesSubmitOutput
>(submitEnrollmentJointFinancesData)
